import type { ContractStatusContract } from "../api/contractStatus";

/**
 * FT-5702 Part 4 — selecting the commodity contract the end-of-contract flow
 * is actually allowed to end, out of ALL the contracts `/api/v1/contracts/status`
 * returns for one SDP.
 *
 * The portfolio (`/allservices`) returns exactly ONE contract per SDP, so the
 * EOC list could never see a second, genuinely open contract sitting behind a
 * stale sibling. This module is the rule that replaces that single row.
 */

/**
 * A contract end date that means "this contract does NOT end" — i.e. the
 * contract is a candidate to be ended.
 *
 * ⚠️ `d == null` ALONE IS WRONG. The repo-wide convention (BE:
 * `end_date != null && end_date !== "0000-00-00"`; FE: `isEndingDate()`) is
 * that MySQL's zero date is "no date". `dateStrings` hands `'0000-00-00'`
 * through verbatim, so a bare null check would classify a zero-date row as
 * "already ending" and SILENTLY HIDE AN ENDABLE CONTRACT — the exact failure
 * class FT-5702 exists to remove.
 */
export const isOpenEnded = (d: string | null | undefined): boolean =>
  d == null || d === "0000-00-00";

/**
 * Same ordering, same six values, as the backend's
 * `COMMODITY_STATUS_PRIORITY` (`functions/contractStatus.ts`) — which is what
 * `mostRelevantContractIdSubquery` ranks the portfolio with. Sharing the
 * ordering verbatim is the point: when an SDP carries more than one candidate,
 * EOC and the portfolio pick the SAME row BY CONSTRUCTION and can never offer
 * the user a contract they disagree about. Do not reorder here alone.
 */
export const COMMODITY_STATUS_PRIORITY = [
  "active",
  "future",
  "unsigned",
  "expired",
  "ended",
  "unknown",
] as const;

/** Unknown/unlisted statuses rank last — never ahead of a value we do know. */
const priorityRank = (status: string): number => {
  const i = (COMMODITY_STATUS_PRIORITY as readonly string[]).indexOf(status);
  return i === -1 ? COMMODITY_STATUS_PRIORITY.length : i;
};

/**
 * Priority first, then `contract_id` DESC — the backend tie-break, so the two
 * sides stay consistent. Pure: the input array is never mutated.
 */
export const rankCommodityContracts = (
  contracts: ContractStatusContract[],
): ContractStatusContract[] =>
  [...contracts].sort((a, b) => {
    const byPriority = priorityRank(a.status) - priorityRank(b.status);
    if (byPriority !== 0) return byPriority;
    return b.contract_id - a.contract_id;
  });

export interface EocContractCandidate {
  /** The contract EOC reasons about for this SDP. */
  contract: ContractStatusContract;
  /**
   * The end date to DISPLAY for the leg. `null` whenever at least one
   * open-ended contract exists (nothing is ending), otherwise the chosen
   * contract's own end date — which keeps the "Already ending — ends <date>"
   * row rendering exactly as it did off the portfolio.
   */
  endDate: string | null;
  /**
   * How many open-ended contracts this SDP carries. `> 1` is a DATA ANOMALY
   * (two live contracts on one SDP), surfaced in the UI rather than silently
   * resolved by the tie-break.
   */
  openEndedCount: number;
}

/**
 * The EOC candidate for one SDP.
 *
 * Order of the rule, and it matters:
 * 1. OPEN-ENDED FIRST (`isOpenEnded`) — a contract with an end date already
 *    set is not a candidate, whatever its status says. This is what makes a
 *    genuinely open contract visible behind an already-ending sibling.
 * 2. Rank the survivors by `COMMODITY_STATUS_PRIORITY`, then `id DESC`.
 * 3. Open-ended is NECESSARY, NOT SUFFICIENT — an `unsigned` / `expired` /
 *    `unknown` contract is open-ended too. The caller still applies the status
 *    rule on top (EOC offers `active` legs only).
 *
 * With no open-ended contract at all, the highest-ranked contract overall is
 * returned WITH its end date, so the leg renders its terminal "already ending"
 * state instead of vanishing. `null` only when the SDP has no contract rows.
 */
export const selectEocContractCandidate = (
  contracts: ContractStatusContract[],
): EocContractCandidate | null => {
  if (contracts.length === 0) return null;
  const open = contracts.filter((c) => isOpenEnded(c.end_date));
  if (open.length > 0) {
    return {
      contract: rankCommodityContracts(open)[0],
      endDate: null,
      openEndedCount: open.length,
    };
  }
  const representative = rankCommodityContracts(contracts)[0];
  return {
    contract: representative,
    endDate: representative.end_date,
    openEndedCount: 0,
  };
};
